import { Image, Product } from "../api/types";
import { unsplashImageUrl } from "../utils/unsplashImage";

const PRODUCT_IMAGE_POOLS: Record<string, string[]> = {
    "objects-Vases": [
        "photo-1631125916276-69bcd14e3980",
        "photo-1631125915510-8ffed5a0d054",
        "photo-1631125915361-b749fdeea230",
        "photo-1631125915902-d8abe9225ff2",
        "photo-1677761640321-b80251be00ca",
        "photo-1631125915732-b98f8774f675",
    ],
    "objects-Ceramics": [
        "photo-1601859785003-1b9760be7cd3",
        "photo-1635166197966-73207e005887",
        "photo-1530006498959-b7884e829a04",
        "photo-1572725350945-a2d24faa89b0",
        "photo-1565972093658-620985899236",
        "photo-1621453571889-0b228e998080",
    ],
    "objects-Decorative Objects": [
        "photo-1665512594386-051aad8b9f68",
        "photo-1655255201488-766303d720a2",
        "photo-1677761821976-4a2b4ed29437",
        "photo-1572853566597-b83cde546912",
        "photo-1631125915973-e0d155a14e4e",
        "photo-1643569556871-91ec60671ed7",
    ],
    "lighting-Table Lamps": [
        "photo-1681412205156-bb506a4ea970",
        "photo-1667312939978-64cf31718a6e",
        "photo-1621177555452-bedbe4c28879",
        "photo-1672166939591-b2547bd18fca",
        "photo-1506377295352-e3154d43ea9e",
        "photo-1753932847231-7949af383b98",
    ],
    "lighting-Floor Lamps": [
        "photo-1761864293821-033e75c1eba3",
        "photo-1731348842542-410be9e4dc7a",
        "photo-1763604535880-eecf2e7895e0",
        "photo-1726389838683-8ff57378c1c5",
        "photo-1771002469956-1932a606a55e",
        "photo-1733060315106-f035d6ce826d",
    ],
    "lighting-Pendant Lights": [
        "photo-1753800558729-7929703f81de",
        "photo-1642260550245-8c36a53b0cfa",
        "photo-1775480392452-2de62ba9a1b1",
        "photo-1580168907206-72a78eeda4ad",
        "photo-1606540037924-ee0470e33abf",
        "photo-1581784878214-8d5596b98a01",
    ],
    "furniture-Side Tables": [
        "photo-1748887522064-5be67910f6db",
        "photo-1750306956970-10d4eec3a9a8",
        "photo-1779367993001-37c7bc75d6b4",
        "photo-1762856490803-8e200418973a",
        "photo-1748887522038-aae7dec76772",
        "photo-1750306955861-4ba2cd84cf07",
    ],
    "furniture-Stools": [
        "photo-1779278547541-370129cc09f7",
        "photo-1719899779266-ae9427607bf1",
        "photo-1537468243621-d21861d29fe3",
        "photo-1612022565287-136b9c669781",
        "photo-1696540008976-b7085c8fb051",
        "photo-1668764450240-3da29796f352",
    ],
    "furniture-Shelving": [
        "photo-1761971975684-9b900192df96",
        "photo-1777734582370-65323e9830c4",
        "photo-1781032392300-ed3bdf78ef4c",
        "photo-1772797583328-f83bc3f94f80",
        "photo-1612366206518-535bea7db163",
        "photo-1781032044213-571bc1faa592",
    ],
    "textiles-Throws": [
        "photo-1565539433134-f41a89c7346e",
        "photo-1598622444897-58985111f62b",
        "photo-1639690222869-1e608aa51f82",
        "photo-1598622444897-58985111f62b",
        "photo-1634665810235-011d663754e7",
        "photo-1734786165046-40caffb38a9a",
    ],
    "textiles-Cushions": [
        "photo-1624351231514-aa7a3189d9cf",
        "photo-1573736860558-79527a353222",
        "photo-1595500403311-02b6a50c9109",
        "photo-1767584394169-cb62eccfe930",
        "photo-1696774276390-6ce82111140f",
        "photo-1736901217654-2fcf7a60c5c7",
    ],
    "textiles-Rugs": [
        "photo-1714926618653-39de3cf5b691",
        "photo-1767161425522-23a79d10b017",
        "photo-1771838455786-bfe3da93d961",
        "photo-1758887263106-48f9934c1cdb",
        "photo-1728129456073-7eda85113862",
        "photo-1583847268964-b28dc8f51f92",
    ],
};

interface VariantInput {
    title: string;
    price: number;
    compareAtPrice?: number;
    quantityAvailable: number;
    sku: string;
}

interface ProductInput {
    id: number;
    handle: string;
    title: string;
    description: string;
    bullets: string[];
    productType: string;
    category: "objects" | "lighting" | "furniture" | "textiles";
    extraTags?: string[];
    vendor: string;
    material: string;
    dimensions: string;
    imageSeed: string;
    imageCount?: number;
    createdAt: string;
    optionName: string;
    variants: VariantInput[];
}

function buildImages(category: string, productType: string, seed: string, count: number): Image[] {
    const pool = PRODUCT_IMAGE_POOLS[`${category}-${productType}`] ?? [];
    const offset = Array.from(seed).reduce((sum, char) => sum + char.charCodeAt(0), 0);
    return Array.from({ length: count }, (_, i) => ({
        url: unsplashImageUrl(pool[(offset + i) % pool.length], 900, 900),
        altText: null,
        width: 900,
        height: 900,
    }));
}

function money(amount: number): Product["priceRange"]["minVariantPrice"] {
    return { amount: amount.toFixed(2), currencyCode: "EUR" };
}

function buildProduct(input: ProductInput): Product {
    const images = buildImages(input.category, input.productType, input.imageSeed, input.imageCount ?? 3);
    const prices = input.variants.map((v) => v.price);
    const compareAtPrices = input.variants
        .map((v) => v.compareAtPrice)
        .filter((v): v is number => typeof v === "number");

    const variants = input.variants.map((v, i) => ({
        id: `gid://shopify/ProductVariant/${input.id}0${i + 1}`,
        title: v.title,
        availableForSale: v.quantityAvailable > 0,
        quantityAvailable: v.quantityAvailable,
        price: money(v.price),
        compareAtPrice: v.compareAtPrice ? money(v.compareAtPrice) : null,
        selectedOptions: [{ name: input.optionName, value: v.title }],
        image: null,
        sku: v.sku,
    }));

    return {
        id: `gid://shopify/Product/${input.id}`,
        handle: input.handle,
        title: input.title,
        description: input.description,
        descriptionHtml: `<p>${input.description}</p><ul>${input.bullets
            .map((b) => `<li>${b}</li>`)
            .join("")}</ul>`,
        productType: input.productType,
        tags: [input.category, ...(input.extraTags ?? [])],
        vendor: input.vendor,
        images,
        featuredImage: images[0] ?? null,
        priceRange: {
            minVariantPrice: money(Math.min(...prices)),
            maxVariantPrice: money(Math.max(...prices)),
        },
        compareAtPriceRange:
            compareAtPrices.length > 0
                ? {
                      minVariantPrice: money(Math.min(...compareAtPrices)),
                      maxVariantPrice: money(Math.max(...compareAtPrices)),
                  }
                : null,
        options: [{ id: `gid://shopify/ProductOption/${input.id}`, name: input.optionName, values: input.variants.map((v) => v.title) }],
        variants,
        availableForSale: variants.some((v) => v.availableForSale),
        createdAt: input.createdAt,
        material: input.material,
        dimensions: input.dimensions,
    };
}

const productInputs: ProductInput[] = [
    // ---- Objects: Vases ----
    {
        id: 1,
        handle: "forma-vase",
        title: "Forma Vase",
        description: "A sculptural stoneware vase with a soft, hand-thrown silhouette. Each piece is individually glazed, so subtle variations in tone are part of its character.",
        bullets: ["Hand-thrown stoneware", "Individually glazed — natural tonal variation", "Watertight, suitable for fresh flowers"],
        productType: "Vases",
        category: "objects",
        extraTags: ["ceramic", "handmade"],
        vendor: "Maison Noma",
        material: "Hand-glazed stoneware",
        dimensions: "H 28 × Ø 14 cm",
        imageSeed: "forma-vase-sand",
        createdAt: "2026-06-10",
        optionName: "Finish",
        variants: [
            { title: "Sand", price: 89, quantityAvailable: 12, sku: "VAS-FRM-SND" },
            { title: "Charcoal", price: 89, quantityAvailable: 9, sku: "VAS-FRM-CHR" },
        ],
    },
    {
        id: 2,
        handle: "terra-vase",
        title: "Terra Vase",
        description: "A tall, tapered vase in unglazed terracotta, celebrating the raw warmth of natural clay. Equally striking empty or filled with dried stems.",
        bullets: ["Unglazed terracotta", "Tapered silhouette", "Best suited to dried botanicals"],
        productType: "Vases",
        category: "objects",
        extraTags: ["ceramic", "terracotta"],
        vendor: "Maison Noma",
        material: "Unglazed terracotta clay",
        dimensions: "H 36 × Ø 12 cm",
        imageSeed: "terra-vase-tall",
        createdAt: "2026-05-22",
        optionName: "Size",
        variants: [{ title: "One Size", price: 68, quantityAvailable: 18, sku: "VAS-TRR-STD" }],
    },
    // ---- Objects: Ceramics ----
    {
        id: 3,
        handle: "nordic-bowl-set",
        title: "Nordic Bowl Set",
        description: "A set of three nesting stoneware bowls, glazed in muted tones. Equally at home serving dinner or gathered on an open shelf.",
        bullets: ["Set of 3 nesting bowls", "Dishwasher and microwave safe", "Muted, food-safe glaze"],
        productType: "Ceramics",
        category: "objects",
        extraTags: ["ceramic", "tableware"],
        vendor: "Maison Noma",
        material: "Glazed stoneware",
        dimensions: "Ø 12 / 16 / 20 cm",
        imageSeed: "nordic-bowl-set",
        createdAt: "2026-07-02",
        optionName: "Glaze",
        variants: [
            { title: "Sand", price: 58, quantityAvailable: 20, sku: "CER-NRD-SND" },
            { title: "Stone Grey", price: 58, quantityAvailable: 14, sku: "CER-NRD-GRY" },
        ],
    },
    {
        id: 4,
        handle: "stoneware-plate-set",
        title: "Stoneware Plate Set",
        description: "A set of four dinner plates in hand-finished stoneware, with a gently irregular rim that catches the light.",
        bullets: ["Set of 4 dinner plates", "Hand-finished rim", "Dishwasher safe"],
        productType: "Ceramics",
        category: "objects",
        extraTags: ["ceramic", "tableware"],
        vendor: "Maison Noma",
        material: "Hand-finished stoneware",
        dimensions: "Ø 27 cm",
        imageSeed: "stoneware-plate-set",
        createdAt: "2026-04-18",
        optionName: "Glaze",
        variants: [{ title: "Sand", price: 72, compareAtPrice: 86, quantityAvailable: 16, sku: "CER-PLT-SND" }],
    },
    // ---- Objects: Decorative Objects ----
    {
        id: 5,
        handle: "sculptural-candle-holder",
        title: "Sculptural Candle Holder",
        description: "A solid brass candle holder with a fluid, organic form — a quiet sculptural moment for a console or dining table.",
        bullets: ["Solid brass, hand-polished", "Fits standard taper candles", "Develops a natural patina over time"],
        productType: "Decorative Objects",
        category: "objects",
        extraTags: ["brass", "handmade"],
        vendor: "Maison Noma",
        material: "Solid brass",
        dimensions: "H 18 × Ø 9 cm",
        imageSeed: "candle-holder-brass",
        createdAt: "2026-06-30",
        optionName: "Finish",
        variants: [
            { title: "Brushed Brass", price: 45, quantityAvailable: 22, sku: "DEC-CDL-BRS" },
            { title: "Blackened Steel", price: 49, quantityAvailable: 11, sku: "DEC-CDL-BLK" },
        ],
    },
    {
        id: 6,
        handle: "ceramic-tray",
        title: "Ceramic Tray",
        description: "A low, wide ceramic tray for keys, jewellery or the everyday objects worth looking at twice.",
        bullets: ["Hand-glazed ceramic", "Non-slip cork base", "Doubles as a catch-all or serving tray"],
        productType: "Decorative Objects",
        category: "objects",
        extraTags: ["ceramic", "handmade"],
        vendor: "Maison Noma",
        material: "Glazed ceramic, cork base",
        dimensions: "30 × 20 × 3 cm",
        imageSeed: "ceramic-tray-oval",
        createdAt: "2026-03-14",
        optionName: "Finish",
        variants: [{ title: "Sand", price: 52, quantityAvailable: 15, sku: "DEC-TRY-SND" }],
    },
    // ---- Lighting: Table Lamps ----
    {
        id: 7,
        handle: "noma-table-lamp",
        title: "Noma Table Lamp",
        description: "Our signature table lamp, pairing a brushed brass base with a hand-sewn linen shade for a warm, diffused glow.",
        bullets: ["Brushed brass base", "Hand-sewn linen shade", "In-line dimmer switch included"],
        productType: "Table Lamps",
        category: "lighting",
        extraTags: ["brass", "linen"],
        vendor: "Maison Noma",
        material: "Brushed brass, linen shade",
        dimensions: "H 46 × Ø 20 cm",
        imageSeed: "noma-table-lamp",
        createdAt: "2026-07-15",
        optionName: "Finish",
        variants: [{ title: "Brushed Brass", price: 129, quantityAvailable: 20, sku: "LMP-NOM-STD" }],
    },
    {
        id: 8,
        handle: "arc-table-lamp",
        title: "Arc Table Lamp",
        description: "A gently curved table lamp in solid oak and opal glass, casting a soft ambient light in any reading corner.",
        bullets: ["Solid oak stem", "Opal glass diffuser", "EU plug, dimmable-compatible bulb (not included)"],
        productType: "Table Lamps",
        category: "lighting",
        extraTags: ["oak", "glass"],
        vendor: "Maison Noma",
        material: "Solid oak, opal glass",
        dimensions: "H 52 × Ø 16 cm",
        imageSeed: "arc-table-lamp",
        createdAt: "2026-05-05",
        optionName: "Finish",
        variants: [
            { title: "Natural Oak", price: 149, quantityAvailable: 13, sku: "LMP-ARC-OAK" },
            { title: "Smoked Oak", price: 159, quantityAvailable: 8, sku: "LMP-ARC-SMK" },
        ],
    },
    // ---- Lighting: Floor Lamps ----
    {
        id: 9,
        handle: "curve-floor-lamp",
        title: "Curve Floor Lamp",
        description: "A sweeping arc floor lamp that brings a soft, sculptural silhouette to any living space — statement lighting without the noise.",
        bullets: ["Powder-coated steel arc", "Weighted marble-effect base", "Foot-pedal switch"],
        productType: "Floor Lamps",
        category: "lighting",
        extraTags: ["steel", "statement"],
        vendor: "Maison Noma",
        material: "Powder-coated steel, marble-effect base",
        dimensions: "H 175 × Base Ø 30 cm",
        imageSeed: "curve-floor-lamp",
        createdAt: "2026-06-20",
        optionName: "Finish",
        variants: [{ title: "Matte Black", price: 249, compareAtPrice: 289, quantityAvailable: 7, sku: "LMP-CRV-BLK" }],
    },
    {
        id: 10,
        handle: "sculpt-floor-lamp",
        title: "Sculpt Floor Lamp",
        description: "A tall, minimal floor lamp with a fabric shade that diffuses light evenly across a room — quietly confident in any corner.",
        bullets: ["Solid ash wood stand", "Cotton-linen blend shade", "Foot switch, EU plug"],
        productType: "Floor Lamps",
        category: "lighting",
        extraTags: ["wood", "linen"],
        vendor: "Maison Noma",
        material: "Solid ash, cotton-linen shade",
        dimensions: "H 158 × Ø 38 cm",
        imageSeed: "sculpt-floor-lamp",
        createdAt: "2026-02-27",
        optionName: "Finish",
        variants: [{ title: "Natural Ash", price: 279, quantityAvailable: 6, sku: "LMP-SCL-ASH" }],
    },
    // ---- Lighting: Pendant Lights ----
    {
        id: 11,
        handle: "orb-pendant-light",
        title: "Orb Pendant Light",
        description: "A cluster of hand-blown glass orbs suspended at varying heights, for a soft, gallery-like glow above a dining table.",
        bullets: ["Hand-blown glass orbs", "Adjustable cable length", "Ceiling canopy included"],
        productType: "Pendant Lights",
        category: "lighting",
        extraTags: ["glass", "statement"],
        vendor: "Maison Noma",
        material: "Hand-blown glass, brass fittings",
        dimensions: "Drop 60–120 cm, Ø 15 cm per orb",
        imageSeed: "orb-pendant-light",
        createdAt: "2026-07-25",
        optionName: "Finish",
        variants: [
            { title: "Clear Glass", price: 159, quantityAvailable: 10, sku: "PND-ORB-CLR" },
            { title: "Smoked Glass", price: 169, quantityAvailable: 6, sku: "PND-ORB-SMK" },
        ],
    },
    {
        id: 12,
        handle: "halo-pendant-light",
        title: "Halo Pendant Light",
        description: "A wide, shallow pendant in spun aluminium, casting a warm ring of light downward. Best hung low over a kitchen island.",
        bullets: ["Spun aluminium shade", "Matte interior finish for even light spread", "Adjustable drop"],
        productType: "Pendant Lights",
        category: "lighting",
        extraTags: ["metal", "kitchen"],
        vendor: "Maison Noma",
        material: "Spun aluminium",
        dimensions: "Ø 40 × H 12 cm",
        imageSeed: "halo-pendant-light",
        createdAt: "2026-04-09",
        optionName: "Finish",
        variants: [{ title: "Matte Black", price: 189, quantityAvailable: 9, sku: "PND-HLO-BLK" }],
    },
    // ---- Furniture: Side Tables ----
    {
        id: 13,
        handle: "arco-side-table",
        title: "Arco Side Table",
        description: "A sculptural side table in solid walnut, its gently curved legs designed to be admired from every angle.",
        bullets: ["Solid walnut construction", "Hand-finished with natural oil", "Assembly required (tool included)"],
        productType: "Side Tables",
        category: "furniture",
        extraTags: ["walnut", "solid-wood"],
        vendor: "Maison Noma",
        material: "Solid walnut",
        dimensions: "H 45 × Ø 40 cm",
        imageSeed: "arco-side-table",
        createdAt: "2026-05-30",
        optionName: "Finish",
        variants: [{ title: "Natural Walnut", price: 320, quantityAvailable: 8, sku: "FRN-ARC-WAL" }],
    },
    {
        id: 14,
        handle: "ellipse-side-table",
        title: "Ellipse Side Table",
        description: "An elliptical side table in travertine-effect stone composite, pairing the weight of stone with a refined, modern form.",
        bullets: ["Stone composite top", "Powder-coated steel base", "Indoor use only"],
        productType: "Side Tables",
        category: "furniture",
        extraTags: ["stone", "steel"],
        vendor: "Maison Noma",
        material: "Stone composite, steel base",
        dimensions: "H 50 × 45 × 35 cm",
        imageSeed: "ellipse-side-table",
        createdAt: "2026-03-11",
        optionName: "Finish",
        variants: [{ title: "Travertine", price: 289, quantityAvailable: 5, sku: "FRN-ELP-TRV" }],
    },
    // ---- Furniture: Stools ----
    {
        id: 15,
        handle: "solid-oak-stool",
        title: "Solid Oak Stool",
        description: "A low stool turned from a single block of solid oak — equally useful as extra seating, a plant stand or a bedside perch.",
        bullets: ["Solid turned oak", "Natural oil finish", "No assembly required"],
        productType: "Stools",
        category: "furniture",
        extraTags: ["oak", "solid-wood"],
        vendor: "Maison Noma",
        material: "Solid oak",
        dimensions: "H 45 × Ø 32 cm",
        imageSeed: "solid-oak-stool",
        createdAt: "2026-06-05",
        optionName: "Finish",
        variants: [{ title: "Natural Oak", price: 145, quantityAvailable: 14, sku: "FRN-STL-OAK" }],
    },
    {
        id: 16,
        handle: "sculpted-stool",
        title: "Sculpted Stool",
        description: "A boldly sculptural stool in solid ash, its hourglass form as compelling as an art object as it is a seat.",
        bullets: ["Solid ash construction", "Hand-sanded, oiled finish", "Weight capacity 120 kg"],
        productType: "Stools",
        category: "furniture",
        extraTags: ["ash", "solid-wood"],
        vendor: "Maison Noma",
        material: "Solid ash",
        dimensions: "H 42 × Ø 34 cm",
        imageSeed: "sculpted-stool",
        createdAt: "2026-01-20",
        optionName: "Finish",
        variants: [
            { title: "Natural Ash", price: 165, quantityAvailable: 10, sku: "FRN-SCL-ASH" },
            { title: "Smoked Ash", price: 175, quantityAvailable: 6, sku: "FRN-SCL-SMK" },
        ],
    },
    // ---- Furniture: Shelving ----
    {
        id: 17,
        handle: "modular-shelving-unit",
        title: "Modular Shelving Unit",
        description: "An open, modular shelving system in solid oak and blackened steel, designed to grow with your collection of books and objects.",
        bullets: ["Solid oak shelves, steel frame", "Modular — extendable with additional bays", "Professional assembly recommended"],
        productType: "Shelving",
        category: "furniture",
        extraTags: ["oak", "steel"],
        vendor: "Maison Noma",
        material: "Solid oak, blackened steel",
        dimensions: "H 180 × W 90 × D 35 cm",
        imageSeed: "modular-shelving-unit",
        createdAt: "2026-04-27",
        optionName: "Finish",
        variants: [{ title: "Oak / Black Steel", price: 650, compareAtPrice: 720, quantityAvailable: 4, sku: "FRN-SHV-OBK" }],
    },
    {
        id: 18,
        handle: "wall-shelf-trio",
        title: "Wall Shelf Trio",
        description: "A set of three floating shelves in solid walnut, staggered to display books, ceramics and small objects with intention.",
        bullets: ["Set of 3 floating shelves", "Solid walnut", "Hidden mounting bracket, hardware included"],
        productType: "Shelving",
        category: "furniture",
        extraTags: ["walnut", "solid-wood"],
        vendor: "Maison Noma",
        material: "Solid walnut",
        dimensions: "60 / 45 / 30 cm wide, D 18 cm",
        imageSeed: "wall-shelf-trio",
        createdAt: "2026-02-14",
        optionName: "Finish",
        variants: [{ title: "Natural Walnut", price: 210, quantityAvailable: 9, sku: "FRN-SHV-WAL" }],
    },
    // ---- Textiles: Throws ----
    {
        id: 19,
        handle: "linen-throw",
        title: "Linen Throw",
        description: "A generously sized throw in stonewashed linen, softening with every wash — perfect draped over a sofa or the foot of a bed.",
        bullets: ["100% stonewashed linen", "Machine washable", "150 × 200 cm"],
        productType: "Throws",
        category: "textiles",
        extraTags: ["linen"],
        vendor: "Maison Noma",
        material: "100% stonewashed linen",
        dimensions: "150 × 200 cm",
        imageSeed: "linen-throw-natural",
        createdAt: "2026-07-08",
        optionName: "Color",
        variants: [
            { title: "Natural", price: 79, quantityAvailable: 24, sku: "TXT-LIN-NAT" },
            { title: "Clay", price: 79, quantityAvailable: 17, sku: "TXT-LIN-CLY" },
        ],
    },
    {
        id: 20,
        handle: "wool-blend-throw",
        title: "Wool Blend Throw",
        description: "A heavier throw in a soft wool-cotton blend with a subtle herringbone weave, ideal for cooler evenings.",
        bullets: ["Wool-cotton blend", "Herringbone weave", "Dry clean recommended"],
        productType: "Throws",
        category: "textiles",
        extraTags: ["wool"],
        vendor: "Maison Noma",
        material: "70% wool, 30% cotton",
        dimensions: "130 × 180 cm",
        imageSeed: "wool-throw-herringbone",
        createdAt: "2026-01-30",
        optionName: "Color",
        variants: [{ title: "Oatmeal", price: 95, quantityAvailable: 12, sku: "TXT-WOL-OAT" }],
    },
    // ---- Textiles: Cushions ----
    {
        id: 21,
        handle: "boucle-cushion",
        title: "Bouclé Cushion",
        description: "A plush bouclé cushion with a removable, washable cover and a feather-blend inner for a soft, generous fill.",
        bullets: ["Bouclé cover, feather-blend inner", "Removable, washable cover", "Concealed zip"],
        productType: "Cushions",
        category: "textiles",
        extraTags: ["boucle"],
        vendor: "Maison Noma",
        material: "Bouclé cover, feather-blend fill",
        dimensions: "50 × 50 cm",
        imageSeed: "boucle-cushion-cream",
        createdAt: "2026-06-18",
        optionName: "Color",
        variants: [
            { title: "Cream", price: 49, quantityAvailable: 28, sku: "TXT-BCL-CRM" },
            { title: "Terracotta", price: 49, quantityAvailable: 19, sku: "TXT-BCL-TRC" },
        ],
    },
    {
        id: 22,
        handle: "linen-cushion-cover",
        title: "Linen Cushion Cover",
        description: "A simple, well-cut linen cushion cover in a relaxed weave — an easy way to layer texture onto any sofa or bed.",
        bullets: ["100% linen", "Cover only, insert sold separately", "Machine washable"],
        productType: "Cushions",
        category: "textiles",
        extraTags: ["linen"],
        vendor: "Maison Noma",
        material: "100% linen",
        dimensions: "45 × 45 cm",
        imageSeed: "linen-cushion-cover",
        createdAt: "2026-05-14",
        optionName: "Color",
        variants: [{ title: "Sand", price: 39, quantityAvailable: 30, sku: "TXT-LCC-SND" }],
    },
    // ---- Textiles: Rugs ----
    {
        id: 23,
        handle: "handwoven-wool-rug",
        title: "Handwoven Wool Rug",
        description: "A handwoven rug in undyed wool, its subtle texture and irregular weave the mark of true craftsmanship underfoot.",
        bullets: ["100% handwoven wool", "Undyed, natural tones", "Professional cleaning recommended"],
        productType: "Rugs",
        category: "textiles",
        extraTags: ["wool", "handmade"],
        vendor: "Maison Noma",
        material: "100% undyed wool",
        dimensions: "200 × 300 cm",
        imageSeed: "handwoven-wool-rug",
        createdAt: "2026-03-25",
        optionName: "Size",
        variants: [
            { title: "170 × 240 cm", price: 320, quantityAvailable: 6, sku: "TXT-RUG-M" },
            { title: "200 × 300 cm", price: 420, quantityAvailable: 4, sku: "TXT-RUG-L" },
        ],
    },
    {
        id: 24,
        handle: "jute-blend-rug",
        title: "Jute Blend Rug",
        description: "A natural-fibre rug woven from jute and cotton, bringing warmth and texture underfoot without overwhelming a room.",
        bullets: ["Jute-cotton blend", "Flat weave", "Not recommended for high-moisture areas"],
        productType: "Rugs",
        category: "textiles",
        extraTags: ["jute"],
        vendor: "Maison Noma",
        material: "Jute-cotton blend",
        dimensions: "160 × 230 cm",
        imageSeed: "jute-blend-rug",
        createdAt: "2026-02-08",
        optionName: "Size",
        variants: [{ title: "160 × 230 cm", price: 165, compareAtPrice: 195, quantityAvailable: 11, sku: "TXT-JUT-M" }],
    },
];

export const products: Product[] = productInputs.map(buildProduct);
